import { Trash } from '@cfa/system-icons';
import React from 'react';
import styled from 'styled-components';

import QuantityStepperInput from './QuantityStepperInput';

interface QuantityStepperProps {
  min: number;
  max?: number;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  disabled?: boolean;
  className?: string;
  useTrashCan?: boolean;
}

const QuantityStepper: React.FC<QuantityStepperProps> = ({
  min,
  max = 999,
  quantity,
  onQuantityChange,
  disabled = false,
  className = '',
  useTrashCan = false,
}) => {
  const validatedQty = isNaN(quantity) ? min : quantity;
  const increaseDisabled = disabled || validatedQty >= max;
  const decreaseDisabled = disabled || validatedQty <= min;
  const showTrashCan = min === 0 && validatedQty === 1;
  const plusSign = '+';
  const minuSign = '−'; // Unicode minus U+2212, not a regular dash

  const increaseButton = (
    <IncreaseButton
      data-testid="quantity-increment"
      disabled={increaseDisabled}
      onClick={() => {
        onQuantityChange(validatedQty + 1);
      }}
      type="button"
    >
      <StepperText $disabled={increaseDisabled}>{plusSign}</StepperText>
    </IncreaseButton>
  );

  const decreaseButton = (
    <DecreaseButton
      data-testid="quantity-decrement"
      disabled={decreaseDisabled}
      onClick={() => {
        onQuantityChange(validatedQty - 1);
      }}
      type="button"
    >
      {useTrashCan && showTrashCan ? (
        <Trash />
      ) : (
        <StepperText $disabled={decreaseDisabled}>{minuSign}</StepperText>
      )}
    </DecreaseButton>
  );

  return (
    <WidthContainer className={className}>
      <StyledQuantityStepper $disabled={increaseDisabled}>
        {decreaseButton}
        <QuantityContainer>
          <QuantityStepperInput
            disabled={disabled}
            max={max}
            min={min}
            onChange={onQuantityChange}
            value={validatedQty}
          />
        </QuantityContainer>
        {increaseButton}
      </StyledQuantityStepper>
    </WidthContainer>
  );
};

const WidthContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 98px;
`;

const StyledQuantityStepper = styled.div<{ $disabled: boolean }>`
  display: flex;
  border-radius: 25px;
  height: 32px;
  width: 98px;
  transition: all 0.2s ease;
  background-color: ${(props) => props.theme.colors.secondary};
  margin-right: 5px;
  border: 2px solid ${(props) => props.theme.colors.accent};
`;

const QuantityButton = styled.button`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colors.secondary};
  border: none;
  padding: 0;
  width: 36px;
  outline: none;

  &:disabled {
    cursor: auto;
  }
`;

const IncreaseButton = styled(QuantityButton)`
  border-radius: 0 25px 25px 0;
`;

const DecreaseButton = styled(QuantityButton)`
  border-radius: 25px 0 0 25px;

  svg {
    color: ${(props) => props.theme.colors.tertiary.red};
    width: 16px;
  }
`;

const StepperText = styled.span<{ $disabled: boolean }>`
  color: ${(props) => (props.$disabled ? props.theme.colors.disabled : props.theme.colors.accent)};
  font-weight: normal;
  font-family: ${(props) => props.theme.regularTextFont};
  font-size: 28px;
`;

const QuantityContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
`;

export default React.memo(QuantityStepper);
